import React, { Suspense, useMemo } from 'react';
import StreamingMarkdownErrorBoundary from './StreamingMarkdownErrorBoundary.jsx';
import { CitationLink } from './CitationLink.jsx';

let streamdownPreloadPromise = null;

export function preloadStreamingMarkdownRenderer() {
  if (!streamdownPreloadPromise) {
    streamdownPreloadPromise = import('streamdown').catch((error) => {
      streamdownPreloadPromise = null;
      throw error;
    });
  }
  return streamdownPreloadPromise;
}

void preloadStreamingMarkdownRenderer().catch(() => undefined);

const Streamdown = React.lazy(() => (
  preloadStreamingMarkdownRenderer().then((module) => ({ default: module.Streamdown }))
));

function StreamdownAdapter(props) {
  return <Streamdown {...props} />;
}

function StreamingMarkdownErrorFallback({ content }) {
  return (
    <div className="flare-markdown-content flare-streamdown-content">
      <p>{String(content ?? '')}</p>
    </div>
  );
}

function StreamingMarkdownPending({ color }) {
  return (
    <div
      className="flare-streaming-pending"
      style={{ color }}
    >
      <span className="flare-streaming-prompt" aria-hidden="true" />
      <span>正在组织回复</span>
    </div>
  );
}

function hasMarkdownFormatting(content) {
  return /(^|\n)\s{0,3}(#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```|~~~)|\*\*|__|~~|`|\[[^\]]*\]\(/m.test(String(content ?? ''));
}

export function resolveStreamingMarkdownFallbackMode(content) {
  return hasMarkdownFormatting(content) ? 'pending' : 'text';
}

function StreamingMarkdownLoadingFallback({ content, color }) {
  return resolveStreamingMarkdownFallbackMode(content) === 'pending'
    ? <StreamingMarkdownPending color={color} />
    : <StreamingMarkdownErrorFallback content={content} />;
}

export function StreamingMarkdownRenderer({
  content,
  pending = false,
  streaming = false,
  textStyle = {},
  citationOptions,
}) {
  const components = useMemo(() => ({ a: props => <CitationLink {...props} {...citationOptions} /> }), [citationOptions]);
  if (pending) {
    return <StreamingMarkdownPending color={textStyle.color} />;
  }

  return (
    <div className="flare-streamdown-shell" style={textStyle}>
      <StreamingMarkdownErrorBoundary
        fallback={<StreamingMarkdownErrorFallback content={content} />}
      >
        <Suspense fallback={(
          <StreamingMarkdownLoadingFallback
            color={textStyle.color}
            content={content}
          />
        )}>
          <StreamdownAdapter
            components={components}
            animated={{
              animation: 'blurIn',
              duration: 90,
              easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
              sep: 'char',
              stagger: 2,
            }}
            caret="circle"
            className="flare-markdown-content flare-streamdown-content"
            controls={false}
            dir="auto"
            isAnimating={streaming}
            lineNumbers={false}
            mode={streaming ? 'streaming' : 'static'}
          >
            {String(content ?? '')}
          </StreamdownAdapter>
        </Suspense>
      </StreamingMarkdownErrorBoundary>
    </div>
  );
}
